import { Pressable, StyleSheet, View, Text, type GestureResponderEvent } from "react-native";
import { colors, radii, spacing, typographyScale, webTransitions } from "../../../styles/design-system";
import React, { useRef, useState } from "react";
import { Chat, DotsThree, PushPinSimple, PencilSimple, Trash } from "@phosphor-icons/react";
import CustomText from "../../common/general/CustomText/CustomText";
import { Portal } from "react-native-paper";

const THREAD_CARD_BG = colors.brand[50];
const TITLE_MAX_WIDTH = 135;
const BORDER_RADIUS = 16;
const MIN_HEIGHT = 37;
const MENU_WIDTH = 160;

export interface ThreadCardProps {
  id: string;
  title: string;
  isActive: boolean;
  pinned?: boolean;
  onSelect?: (id: string) => void;
  onPin?: (id: string) => void;
  onRename?: (id: string) => void;
  onDelete?: (id: string) => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}

const ThreadCard: React.FC<ThreadCardProps> = ({
  id,
  title,
  isActive,
  pinned = false,
  onSelect,
  onPin,
  onRename,
  onDelete,
  onMouseEnter,
  onMouseLeave,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const menuTriggerRef = useRef<View>(null);

  const hasActions = onPin || onRename || onDelete;

  const handleMenuToggle = (e?: GestureResponderEvent) => {
    if (e) {
      e.stopPropagation?.();
    }
    if (!menuOpen && menuTriggerRef.current) {
      menuTriggerRef.current.measureInWindow((x, y, width, height) => {
        setMenuPosition({
          top: y + height + 4,
          left: x + width - MENU_WIDTH,
        });
        setMenuOpen(true);
      });
    } else {
      setMenuOpen(false);
    }
  };

  const handleMenuClose = () => {
    setMenuOpen(false);
    setIsHovered(false);
  };

  const handlePin = () => {
    onPin?.(id);
    handleMenuClose();
  };

  const handleRename = () => {
    onRename?.(id);
    handleMenuClose();
  };

  const handleDelete = () => {
    onDelete?.(id);
    handleMenuClose();
  };

  const shouldShowBackground = isActive || isHovered;
  const showMenuButton = isHovered && hasActions;

  return (
    <View
      style={[styles.card, shouldShowBackground && styles.cardWithBackground]}
      {...(onMouseEnter && onMouseLeave ? { onMouseEnter, onMouseLeave } : {})}>
      <Pressable
        style={styles.main}
        onPress={() => onSelect?.(id)}
        onHoverIn={() => setIsHovered(true)}
        onHoverOut={() => setIsHovered(false)}
        accessibilityRole="button"
        accessibilityLabel={title}>
        <View style={styles.iconWrap}>
          <Chat size={16} color={isActive ? colors.brand[700] : colors.grey[500]} />
        </View>
        <Text style={[styles.title, isActive && styles.titleActive]} numberOfLines={1} ellipsizeMode="tail">
          {title}
        </Text>
      </Pressable>
      {hasActions && (
        <View
          style={styles.menuContainer}
          {...{
            onMouseEnter: () => setIsHovered(true),
            onMouseLeave: () => !menuOpen && setIsHovered(false),
          }}>
          <Pressable
            ref={menuTriggerRef}
            style={[
              styles.menuTrigger,
              { opacity: showMenuButton || menuOpen ? 1 : 0 },
              (showMenuButton || menuOpen) && styles.menuTriggerVisible,
            ]}
            onPress={handleMenuToggle}
            accessibilityRole="button"
            accessibilityLabel="Thread options">
            <DotsThree size={14} color={colors.grey[500]} weight="bold" />
          </Pressable>
          {menuOpen && (
            <Portal>
              <Pressable style={styles.menuOverlay} onPress={handleMenuClose} accessibilityLabel="Close menu" />
              <View style={[styles.menu, { top: menuPosition.top, left: menuPosition.left }]}>
                {onPin && (
                  <Pressable
                    style={(state) => {
                      const { pressed } = state;
                      const hovered = (state as { hovered?: boolean }).hovered ?? false;
                      return [styles.menuItem, (hovered || pressed) && styles.menuItemHovered];
                    }}
                    onPress={handlePin}
                    accessibilityRole="button"
                    accessibilityLabel={pinned ? "Unpin thread" : "Pin thread"}>
                    <PushPinSimple size={14} color={colors.grey[900]} />
                    <CustomText
                      text={pinned ? "Unpin" : "Pin"}
                      size="xs"
                      weight="medium"
                      useTranslationText={false}
                      style={styles.menuItemText}
                    />
                  </Pressable>
                )}
                {onRename && (
                  <Pressable
                    style={(state) => {
                      const { pressed } = state;
                      const hovered = (state as { hovered?: boolean }).hovered ?? false;
                      return [styles.menuItem, (hovered || pressed) && styles.menuItemHovered];
                    }}
                    onPress={handleRename}
                    accessibilityRole="button"
                    accessibilityLabel="Rename thread">
                    <PencilSimple size={14} color={colors.grey[900]} />
                    <CustomText
                      text="Rename"
                      size="xs"
                      weight="medium"
                      useTranslationText={false}
                      style={styles.menuItemText}
                    />
                  </Pressable>
                )}
                {onDelete && (
                  <Pressable
                    style={(state) => {
                      const { pressed } = state;
                      const hovered = (state as { hovered?: boolean }).hovered ?? false;
                      return [styles.menuItem, (hovered || pressed) && styles.menuItemHovered];
                    }}
                    onPress={handleDelete}
                    accessibilityRole="button"
                    accessibilityLabel="Delete thread">
                    <Trash size={14} color={colors.error[600]} />
                    <CustomText
                      text="Delete"
                      size="xs"
                      weight="medium"
                      useTranslationText={false}
                      style={styles.menuItemTextDanger}
                    />
                  </Pressable>
                )}
              </View>
            </Portal>
          )}
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  card: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    minHeight: MIN_HEIGHT,
    paddingVertical: 6,
    paddingHorizontal: 12,
    borderRadius: BORDER_RADIUS,
    gap: spacing.xs,
    minWidth: 0,
  },
  cardWithBackground: {
    backgroundColor: THREAD_CARD_BG,
  },
  main: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
    gap: spacing.xs,
    minWidth: 0,
  },
  iconWrap: {
    flexShrink: 0,
  },
  title: {
    flex: 1,
    maxWidth: TITLE_MAX_WIDTH,
    fontSize: typographyScale.textSm.fontSize,
    fontFamily: "Inter",
    fontWeight: "500",
    color: colors.grey[700],
    minWidth: 0,
  },
  titleActive: {
    color: colors.brand[700],
  },
  menuContainer: {
    position: "relative",
    flexShrink: 0,
  },
  menuTrigger: {
    padding: 6,
    marginRight: 4,
    borderRadius: 6,
    alignItems: "center",
    justifyContent: "center",
    ...webTransitions.fast,
  },
  menuTriggerVisible: {
    backgroundColor: colors.grey[200],
  },
  menuOverlay: {
    // @ts-expect-error - works for web
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 9998,
    backgroundColor: "transparent",
  },
  menu: {
    position: "absolute",
    backgroundColor: colors.white,
    borderRadius: radii.sm,
    borderWidth: 1,
    borderColor: colors.grey[200],
    paddingVertical: 4,
    paddingHorizontal: 4,
    width: MENU_WIDTH,
    shadowColor: colors.grey[900],
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 12,
    elevation: 8,
    zIndex: 10000,
  },
  menuItem: {
    flexDirection: "row",
    alignItems: "center",
    gap: spacing.xs,
    paddingVertical: 6,
    paddingHorizontal: 8,
    borderRadius: radii.xs,
    ...webTransitions.fast,
  },
  menuItemHovered: {
    backgroundColor: colors.grey[50],
  },
  menuItemText: {
    fontSize: 14,
    fontFamily: "Inter",
    color: colors.grey[900],
  },
  menuItemTextDanger: {
    fontSize: typographyScale.textSm.fontSize,
    fontFamily: "Inter",
    color: colors.error[600],
  },
});

export default ThreadCard;
